import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Ruler, Search } from 'lucide-react'
import {
  BODY_FIELDS,
  fetchMeasuringClass,
  isMeasured,
  matching,
  freshness,
  outstanding,
  saveBodyMeasurements,
  studioToday,
  type BodyMeasurements,
  type MeasuringRow,
} from '../api/measuring'
import { measurementAge } from '../api/dancerAdmin'
import { today } from '../api/calendar'

/**
 * Measuring a whole class in one sitting (D-440).
 *
 * <b>A route rather than a dialog</b>, chosen at the gate. Three things follow
 * from it and none were available inside the roster modal: full viewport height
 * for 64 rows with a form open on a phone; one shell for the teacher's way in
 * and Tonya's, instead of a 32rem dialog and a 30rem drawer that would drift;
 * and a URL, which is what makes "come back on Thursday" something the browser
 * remembers rather than something she re-navigates to.
 *
 * <b>No wizard, no next, no skip</b> (D-444). The obvious build for "measure
 * them all" advances one dancer at a time, and it would be wrong: `asked`, the
 * queue is whoever is standing in front of her, not roster order and not
 * alphabetical. A filter box reaches a named dancer in two taps and has no
 * position to lose when she is interrupted.
 */
export default function MeasuringView() {
  const { classId = '' } = useParams()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState<string | null>(null)

  const { data, isPending, isError } = useQuery({
    queryKey: ['measuring', classId],
    queryFn: () => fetchMeasuringClass(classId),
  })

  const rows = useMemo(() => matching(data?.dancers ?? [], query), [data, query])

  if (isPending) return <p className="page-shell py-8 text-[13px] text-ink-3">Loading…</p>

  if (isError || !data) {
    return (
      <p className="page-shell py-8 text-[13px] text-ink-3">
        That class could not be loaded. It may have been removed.
      </p>
    )
  }

  const left = outstanding(data.dancers)

  return (
    <div className="page-shell flex min-h-full flex-col gap-4 py-6">
      <header className="flex flex-col gap-1">
        <h1 className="font-display text-2xl wrap-anywhere">{data.name}</h1>
        <p className="text-[13px] text-ink-3">
          {/*
            Says what is left rather than what is done, because that is the
            question she is holding a tape to answer. Counted over the whole
            class, never the filtered view - a number that moved when she typed
            would be answering a question nobody asked.
          */}
          {data.dancers.length === 0
            ? 'Nobody is in this class yet.'
            : left === 0
              ? `All ${data.dancers.length} measured.`
              : `${left} of ${data.dancers.length} still to measure.`}
        </p>
      </header>

      {data.dancers.length > 0 && (
        <label className="relative flex items-center">
          <Search size={15} className="pointer-events-none absolute left-3 text-ink-3" />
          <span className="sr-only">Find a dancer</span>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type a name"
            className="w-full rounded-xl border border-line bg-surface py-2.5 pl-9 pr-3 text-[14px]"
          />
        </label>
      )}

      {data.dancers.length > 0 && rows.length === 0 && (
        <p className="text-[13px] text-ink-3">
          {/*
            Names what was typed, because the filter is how she reaches somebody
            and "no dancers" would read as an empty class.
          */}
          Nobody in this class matches &ldquo;{query.trim()}&rdquo;.
        </p>
      )}

      <ul className="flex flex-col gap-1.5">
        {rows.map((row) => (
          <Row
            key={row.dancerId}
            row={row}
            classId={classId}
            open={open === row.dancerId}
            onOpen={() => setOpen(open === row.dancerId ? null : row.dancerId)}
          />
        ))}
      </ul>
    </div>
  )
}

function Row({
  row,
  classId,
  open,
  onOpen,
}: {
  row: MeasuringRow
  classId: string
  open: boolean
  onOpen: () => void
}) {
  const queryClient = useQueryClient()

  /*
    Seeded from what she already holds, which is the whole data-loss fix
    (D-442). A dancer with imported figures and no date reads as "not measured"
    and is not empty; opening her blank would delete a real waist and hip on
    the first girth anybody saved.
  */
  const [form, setForm] = useState<BodyMeasurements>(row.measurements)

  const save = useMutation({
    mutationFn: () => saveBodyMeasurements(row.dancerId, form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['measuring', classId] })
      // The dancer's own record shows the same figures (D-439's lesson).
      queryClient.invalidateQueries({ queryKey: ['dancer', row.dancerId] })
      onOpen()
    },
  })

  /*
    The studio's today (D-386), parsed to local midnight the same way
    `measurementAge` parses the stored date (D-455). Handing it a UTC midnight
    put the comparison seven hours behind in Central, so every row read a day
    ahead of itself and said "Dated in the future" the moment it was saved.
  */
  const now = studioToday(today())
  const age = measurementAge(row.measuredOn, now)
  const state = freshness(row, now)

  return (
    <li className="overflow-hidden rounded-xl border border-line bg-surface">
      <button
        type="button"
        onClick={onOpen}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-surface-2"
      >
        {/*
          A tick, so the eye can count what is left without reading 64
          sentences (D-455). Only on a recent measurement: a green tick beside
          "measured 11 months ago" would be the screen contradicting itself.
        */}
        {state === 'recent' && (
          <Check size={15} strokeWidth={3} className="shrink-0 text-good" aria-hidden="true" />
        )}

        <span className="min-w-0 flex-1 text-[14px] font-semibold wrap-anywhere">{row.name}</span>

        {/*
          The words stay, and they say how old rather than "done". A figure
          with no age reads as current however old it is, which is what the
          date exists to prevent - so the colour carries "finished" and the
          sentence carries "when".
        */}
        <span
          className={`shrink-0 text-[12px] ${state === 'recent' ? 'font-semibold text-good' : 'text-ink-3'}`}
        >
          {isMeasured(row) ? age : 'Not measured'}
        </span>
      </button>

      {open && (
        <div className="border-t border-line px-4 py-3">
          {row.mayRecord ? (
            <>
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                {BODY_FIELDS.map((field) => (
                  <label key={field.key} className="flex flex-col gap-1">
                    <span className="text-[12px] font-semibold text-ink-3">{field.label}</span>
                    <input
                      type="text"
                      value={form[field.key] ?? ''}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, [field.key]: e.target.value || null }))
                      }
                      /*
                        Free text, never a number input (D-198). The real data
                        holds 5'2, 62 in and 62 for one measurement; a numeric
                        field would refuse two of them and invent precision the
                        source does not have.
                      */
                      inputMode="text"
                      className="min-h-[44px] rounded-lg border border-line bg-surface px-3 text-[15px]"
                    />
                  </label>
                ))}
              </div>

              <div className="mt-3 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => save.mutate()}
                  disabled={save.isPending}
                  className="min-h-[44px] rounded-lg bg-accent px-4 text-[14px] font-semibold text-on-accent disabled:opacity-60"
                >
                  {save.isPending ? 'Saving…' : 'Save'}
                </button>

                {save.isError && (
                  <span role="alert" className="text-[12.5px] text-crit">
                    That did not save. Try again.
                  </span>
                )}
              </div>
            </>
          ) : (
            <p className="flex items-center gap-2 text-[13px] text-ink-3">
              <Ruler size={14} className="shrink-0" />
              {/*
                Said rather than hidden. A row that simply had no form would
                read as a broken screen, and she would try again.
              */}
              You are not down as teaching {row.name.split(' ')[0]}, so her
              measurements are somebody else&rsquo;s to record.
            </p>
          )}
        </div>
      )}
    </li>
  )
}
